// ProviderCardListing.tsx
import { Search } from '.generated/Feature.CW.model';
import { useSitecore } from '@sitecore-content-sdk/nextjs';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import { tv } from 'tailwind-variants';
import React, { useEffect, useRef, useState } from 'react';
import { Result } from '@coveo/headless';
import { Data } from '.generated/Foundation.CW.model';
import useDictionary from 'lib/hooks/useDictionary';

// Import our two new extracted modules
import { ProviderCardListingSkeleton } from './ProviderCardListingSkeleton';
import { ProviderCardListingContent } from './ProviderCardListingContent';

export type LocationPageProps = Data.BaseTemplateSections.Location;
export type ProviderCardListingProps = Search.ProviderCardListing & {
  params: { Theme: string; maxNumberOfLocationCards: string };
  isSidenavPart?: boolean;
};

const ProviderCardListing = (props: ProviderCardListingProps) => {
  return (
    <ComponentWrapper
      className="bg-theme-bg provider-listing-wrapper"
      isSidenavPart={props?.isSidenavPart}
    >
      <div className="max-w-screen-xxl mx-auto lg:px-[80px] px-10">
        <ProviderListingRenderer
          maxCards={parseInt(props?.params?.maxNumberOfLocationCards || '4')}
        />
      </div>
    </ComponentWrapper>
  );
};

export default ProviderCardListing;

const ProviderListingRenderer = ({ maxCards }: { maxCards: number }) => {
  const { ProviderProxyImage } = TAILWIND_VARIANTS();
  const clickedToggleRef = useRef(false);
  const { getDictionaryValue } = useDictionary();
  const showAlldataLable = getDictionaryValue('ShowAllStaffLabel');
  const showLessdataLable = getDictionaryValue('ShowLess');
  const { page } = useSitecore();
  const locationPageDetails = page?.layout?.sitecore?.route as LocationPageProps;
  const locationName = locationPageDetails.fields?.locationName?.value;

  const [showAll, setShowAll] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);
  const [results, setResults] = useState<Result[]>([]);
  const [totalResults, setTotalResults] = useState<number>(0);

  // Track loading state explicitly to prevent layouts flashing/shifting
  const [isSearching, setIsSearching] = useState<boolean>(true);

  useEffect(() => {
    const normalizedLocation = locationName?.trim() || '';
    const performSearch = async () => {
      try {
        setIsSearching(true);
        const res = await fetch(`/api/coveo-proxy/location-providers/${normalizedLocation}`);
        if (!res.ok) {
          setIsSearching(false);
          return;
        }
        const { totalCount, results } = (await res.json()) as {
          totalCount: number;
          results: Result[];
        };
        setResults(results);
        setTotalResults(totalCount);
      } catch (error) {
        console.error('Error fetching related blogs:', error);
      } finally {
        setIsSearching(false);
      }
    };
    performSearch();
  }, [locationName]);

  useEffect(() => {
    if (clickedToggleRef.current && !showAll && topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' });
      clickedToggleRef.current = false;
    }
  }, [showAll]);

  const visibleResults = showAll ? results : results.slice(0, maxCards);
  const shouldShowToggle = totalResults && totalResults > maxCards;

  const handleToggleClick = () => {
    clickedToggleRef.current = true;
    setShowAll((prev) => !prev);
  };

  return (
    <div
      ref={topRef}
      className="resultlist-wrapper"
      id="location-staff-list"
      data-hasresult={results?.length > 0 ? 'true' : 'false'}
    >
      {isSearching ? (
        /* State 1: Active Async Fetching */
        <ProviderCardListingSkeleton count={maxCards} />
      ) : results?.length > 0 ? (
        /* State 2: Content Ready */
        <ProviderCardListingContent
          visibleResults={visibleResults}
          shouldShowToggle={shouldShowToggle}
          showAll={showAll}
          showLessdataLable={showLessdataLable}
          showAlldataLable={showAlldataLable}
          totalResults={totalResults}
          onToggleClick={handleToggleClick}
          providerProxyImageClass={ProviderProxyImage()}
        />
      ) : (
        /* State 3: Fallback Fall-through */
        <div>No data found</div>
      )}
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  defaultVariants: { style: 'primary' },
  slots: {
    ProviderProxyImage: [
      'bg-blue-100',
      'rounded-md',
      'mb-[26px]',
      'lg:w-[160px] lg:h-[200px]',
      'md:w-[160px] md:h-[200px]',
      'w-[100px] h-[125px]',
      'flex items-center justify-center',
    ],
  },
  variants: {},
});
